import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { chargerService } from '../services/chargerService';
import { Charger, QueueDetail, Session, StartChargingRequest } from '../types';
import { useApp } from '../contexts/AppContext';
import { UpdateChargingModal } from '../components/UpdateChargingModal';
import { useQueueJoinFlow } from '../hooks/useQueueJoinFlow';
import '../styles/charger-table.css';
import { 
  Users,
  Play,
  UserPlus,
  UserMinus,
  Zap,
  Loader2,
  WifiOff,
  Clock,
  CheckCircle,
  ArrowUp,
  X
} from 'lucide-react';

interface ChargerTableData {
  charger: Charger;
  queueDetails?: QueueDetail;
  currentSession?: Session;
}

export function ChargerTablePage() {
  const { t } = useTranslation();
  const { state } = useApp();
  const [chargersData, setChargersData] = useState<ChargerTableData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [showStartModal, setShowStartModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedCharger, setSelectedCharger] = useState<Charger | null>(null);
  const [startChargingError, setStartChargingError] = useState<string | null>(null);
  const [updateChargingError, setUpdateChargingError] = useState<string | null>(null);
  const [joinQueueError, setJoinQueueError] = useState<string | null>(null);
  const { joinQueue, modals: queueJoinModals } = useQueueJoinFlow();

  useEffect(() => {
    loadChargersData();
  }, []);

  const loadChargersData = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      // Get all chargers
      const chargers = await chargerService.getChargers();
      
      // Load queue details for each charger in parallel
      const chargersWithData = await Promise.all(
        chargers.map(async (charger) => {
          try {
            const queueDetails = await chargerService.getChargerQueue(charger.id);
            return {
              charger,
              queueDetails
            };
          } catch (error) {
            console.error(`Failed to load queue details for charger ${charger.id}:`, error);
            return {
              charger
            };
          }
        })
      );

      console.log('🔍 Loaded chargers data:', chargersWithData);
      
      // Debug queue users for the first charger to see the structure
      if (chargersWithData.length > 0 && chargersWithData[0].queueDetails) {
        console.log('🔍 Sample queue details:', chargersWithData[0].queueDetails);
        console.log('🔍 Sample queue users:', chargersWithData[0].queueDetails.queueUsers);
      }

      setChargersData(chargersWithData);
    } catch (error) {
      console.error('Failed to load chargers data:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadChargersData(true);
  };

  const setChargerLoading = (chargerId: string, loading: boolean) => {
    setActionLoading(prev => ({ ...prev, [chargerId]: loading }));
  };

  // Helper function to check if current user can perform actions on a specific user's cell
  const canUserPerformAction = (targetUser: any) => {
    console.log('🔍 canUserPerformAction called with:', targetUser);
    console.log('🔍 Current state.user:', state.user);
    
    // If no target user exists, no one can perform actions
    if (!targetUser) {
      console.log('🔍 canUserPerformAction: No target user, returning false');
      return false;
    }
    
    // If no current user is logged in, return false
    if (!state.user) {
      console.log('🔍 canUserPerformAction: No current user logged in, returning false');
      return false;
    }
    
    // For queue users, use the isCurrentUser field provided by the API
    if ('isCurrentUser' in targetUser) {
      console.log('🔍 canUserPerformAction (queue user):', {
        targetUserName: targetUser?.fullName,
        isCurrentUser: targetUser?.isCurrentUser,
        hasIsCurrentUserField: true
      });
      
      // If backend properly set isCurrentUser, use it
      if (targetUser.isCurrentUser) {
        return true;
      }
      
      // Fallback: Compare by fullName if backend didn't set isCurrentUser properly
      const nameMatch = state.user.fullName === targetUser.fullName;
      console.log('🔍 Fallback name comparison:', {
        currentUserName: state.user.fullName,
        targetUserName: targetUser.fullName,
        nameMatch
      });
      return nameMatch;
    }
    
    // For current charger user, compare with state.user
    const canPerform = state.user.fullName === targetUser.fullName;
    console.log('🔍 canUserPerformAction (current user):', {
      currentUserName: state.user.fullName,
      targetUserName: targetUser?.fullName,
      canPerform,
      hasIsCurrentUserField: false
    });
    return canPerform;
  };

  // Helper function to check if current user is charging anywhere
  const isUserChargingAnywhere = () => {
    return chargersData.some(data => data.charger.isUserChargingAnywhere);
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleStartCharging = async (charger: Charger) => {
    if (!state.user) return;
    
    // Show the time selection modal instead of directly starting
    setSelectedCharger(charger);
    setShowStartModal(true);
    setStartChargingError(null);
  };

  // New function to handle actual charging start with selected time
  const handleConfirmStartCharging = async (minutes: number) => {
    if (!selectedCharger) return;
    
    try {
      setChargerLoading(selectedCharger.id, true);
      
      const request: StartChargingRequest = {
        estimatedMinutes: minutes
      };
      
      await chargerService.startCharging(selectedCharger.id, request);
      
      // Close modal and refresh data
      setShowStartModal(false);
      setSelectedCharger(null);
      setStartChargingError(null);
      handleRefresh();
    } catch (error) {
      console.error('Failed to start charging:', error);
      setStartChargingError(error instanceof Error ? error.message : 'Failed to start charging');
    } finally {
      setChargerLoading(selectedCharger.id, false);
    }
  };

  const handleUpdateCharging = async (charger: Charger) => {
    // Show the update modal
    setSelectedCharger(charger);
    setShowUpdateModal(true);
    setUpdateChargingError(null);
  };

  const handleConfirmStopCharging = async () => {
    if (!selectedCharger) return;
    
    try {
      setChargerLoading(selectedCharger.id, true);
      await chargerService.stopCharging(selectedCharger.id);
      
      // Close modal and refresh data
      setShowUpdateModal(false);
      setSelectedCharger(null);
      setUpdateChargingError(null);
      handleRefresh();
    } catch (error) {
      console.error('Failed to stop charging:', error);
      setUpdateChargingError(error instanceof Error ? error.message : 'Failed to stop charging');
    } finally {
      setChargerLoading(selectedCharger.id, false);
    }
  };

  const handleConfirmExtendCharging = async (additionalMinutes: number) => {
    if (!selectedCharger) return;
    
    try {
      setChargerLoading(selectedCharger.id, true);
      setUpdateChargingError(null);
      await chargerService.extendCharging(selectedCharger.id, { additionalMinutes });
      
      // Close modal and refresh data
      setShowUpdateModal(false);
      setSelectedCharger(null);
      handleRefresh();
    } catch (error: any) {
      console.error('Failed to extend charging:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = t('errors.generic');
      
      // Check for localization keys from backend validation
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        console.log('Server message:', serverMessage);
        if (serverMessage === 'extensionExceedsMaximum') {
          errorMessage = t('errors.extensionExceedsMaximum');
        } else if (serverMessage === 'extensionBelowMinimum') {
          errorMessage = t('errors.extensionBelowMinimum');
        } else if (serverMessage === 'invalidExtensionDuration') {
          errorMessage = t('errors.invalidExtensionDuration');
        } else if (serverMessage === 'invalidChargingDuration') {
          errorMessage = t('errors.invalidChargingDuration');
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setUpdateChargingError(errorMessage);
    } finally {
      setChargerLoading(selectedCharger.id, false);
    }
  };

  const handleConfirmReduceCharging = async (minutesToReduce: number) => {
    if (!selectedCharger) return;
    
    try {
      setChargerLoading(selectedCharger.id, true);
      setUpdateChargingError(null);
      await chargerService.reduceCharging(selectedCharger.id, { minutesToReduce });
      
      // Close modal and refresh data
      setShowUpdateModal(false);
      setSelectedCharger(null);
      handleRefresh();
    } catch (error: any) {
      console.error('Failed to reduce charging:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = t('errors.generic');
      
      // Check for localization keys from backend validation
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        console.log('Server message:', serverMessage);
        if (serverMessage === 'reductionTimeExceedsMaximum') {
          errorMessage = t('errors.reductionTimeExceedsMaximum');
        } else if (serverMessage === 'reductionBelowMinimumBuffer') {
          errorMessage = t('errors.reductionBelowMinimumBuffer');
        } else if (serverMessage === 'reductionBelowMinimumDuration') {
          errorMessage = t('errors.reductionBelowMinimumDuration');
        } else if (serverMessage === 'invalidReductionDuration') {
          errorMessage = t('errors.invalidReductionDuration');
        } else if (serverMessage === 'invalidChargingDuration') {
          errorMessage = t('errors.invalidChargingDuration');
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setUpdateChargingError(errorMessage);
    } finally {
      setChargerLoading(selectedCharger.id, false);
    }
  };

  const handleJoinQueue = async (charger: Charger, shiftIndex?: number) => {
    try {
      setChargerLoading(charger.id, true);
      setJoinQueueError(null);
      
      await joinQueue(charger.id, shiftIndex);
      
      // Refresh data to show updated status
      handleRefresh();
    } catch (error: any) {
      console.error('Failed to join queue:', error);
      setJoinQueueError(error.response?.data?.message || error.message || t('errors.generic'));
    } finally {
      setChargerLoading(charger.id, false);
    }
  };

  const handleLeaveQueue = async (charger: Charger) => {
    try {
      setChargerLoading(charger.id, true);
      await chargerService.cancelQueue(charger.id);
      
      // Refresh data to show updated status
      handleRefresh();
    } catch (error) {
      console.error('Failed to cancel queue:', error);
      // You might want to show a toast notification here
    } finally {
      setChargerLoading(charger.id, false);
    }
  };

  // const handleAcceptQueue = async (charger: Charger) => {
  //   try {
  //     setChargerLoading(charger.id, true);
      
  //     await chargerService.acceptQueue(charger.id);
      
  //     // Refresh data to show updated status
  //     await loadChargersData();
  //   } catch (error) {
  //     console.error('Failed to accept queue:', error);
  //     // You might want to show a toast notification here
  //   } finally {
  //     setChargerLoading(charger.id, false);
  //   }
  // };

  const getStatusColor = (charger: Charger) => {
    if (!charger.online) return '#ef4444'; // red
    if (charger.status === 'charging') return '#f59e0b'; // amber
    if (charger.status === 'awaitingNext') return '#3b82f6'; // blue
    return '#10b981'; // green (idle)
  };

  const getStatusIcon = (charger: Charger) => {
    if (!charger.online) return <WifiOff size={16} />;
    if (charger.status === 'charging') return <Zap size={16} />;
    if (charger.status === 'awaitingNext') return <Clock size={16} />;
    return <CheckCircle size={16} />;
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    try {
      return new Date(timeString).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return '-';
    }
  };

  if (isLoading && !isRefreshing) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="loader-splash">
            <Zap className="loader-icon animate-pulse" size={64} />
            <Loader2 className="loader-spinner animate-spin" size={32} />
            <h2 className="loader-title">{t('chargerTable.title')}</h2>
            <p className="loading-text">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="charger-table-page">
        {/* Screen Title */}
        <div style={{ 
          textAlign: 'center', 
          margin: '20px 0',
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#1f2937'
        }}>
          {t('chargerTable.title')}
        </div>

        {joinQueueError && (
          <div className="queue-error-message" role="alert">
            <div className="error-alert">
              <X size={16} />
              <span>{joinQueueError}</span>
              <button type="button" className="error-alert-dismiss" onClick={() => setJoinQueueError(null)} aria-label={t('common.close')}>
                <X size={14} />
              </button>
            </div>
          </div>
        )}
        {queueJoinModals}
        
        {/* Split Pivot Tables */}
        
        {/* Top Table: Chargers 221-224 */}
        <div className="excel-table-container">
          <div className="table-wrapper">
            <table className="excel-table pivot-table">
              <thead>
                <tr>
                  <th className="sticky-header row-label-header"></th>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 221 && chargerNumber <= 224;
                    })
                    .map((data) => (
                    <th key={data.charger.id} className="sticky-header charger-column">
                      {data.charger.name.replace(/\D/g, '')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Status Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.status')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 221 && chargerNumber <= 224;
                    })
                    .map((data) => (
                    <td key={`status-${data.charger.id}`} className="pivot-cell status-cell">
                      <div className="status-indicator">
                        <div 
                          className="status-icon"
                          style={{ color: getStatusColor(data.charger) }}
                        >
                          {getStatusIcon(data.charger)}
                        </div>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Current User Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.shift0')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 221 && chargerNumber <= 224;
                    })
                    .map((data) => (
                    <td key={`user-${data.charger.id}`} className="pivot-cell user-cell">
                      {data.charger.currentUser ? (
                        <div className="user-info">
                          <div className="user-name">{data.charger.currentUser.fullName}</div>
                          <div className="user-plate">{data.charger.currentUser.primaryPlate}</div>
                          <button
                            onClick={() => handleUpdateCharging(data.charger)}
                            disabled={actionLoading[data.charger.id] || !canUserPerformAction(data.charger.currentUser)}
                            style={{
                              padding: '4px 8px',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: canUserPerformAction(data.charger.currentUser) ? '#3b82f6' : '#9ca3af',
                              color: 'white',
                              cursor: canUserPerformAction(data.charger.currentUser) ? 'pointer' : 'not-allowed',
                              fontSize: '10px',
                              marginTop: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '2px'
                            }}
                            title="Update Session"
                          >
                            <Clock size={10} />
                            Update
                          </button>
                        </div>
                      ) : (
                        <div className="action-button-container">
                          <button
                            onClick={() => handleStartCharging(data.charger)}
                            disabled={actionLoading[data.charger.id] || false || !data.charger.online || isUserChargingAnywhere()}
                            style={{
                              padding: '6px 12px',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: (data.charger.online && !isUserChargingAnywhere()) ? '#10b981' : '#9ca3af',
                              color: 'white',
                              cursor: (data.charger.online && !isUserChargingAnywhere()) ? 'pointer' : 'not-allowed',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title={isUserChargingAnywhere() ? "Already charging elsewhere" : "Start Charging"}
                          >
                            <Play size={12} />
                            Start
                          </button>
                        </div>
                      )}
                    </td>
                  ))}
                </tr>

                {/* End Time Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.endTime')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 221 && chargerNumber <= 224;
                    })
                    .map((data) => (
                    <td key={`end-${data.charger.id}`} className="pivot-cell time-cell">
                      {data.charger.estimatedEndTime ? (
                        <span className={data.charger.isSessionOverdue ? 'overdue' : ''}>
                          {formatTime(data.charger.estimatedEndTime)}
                        </span>
                      ) : (
                        <span className="empty-cell">-</span>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Queue Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.queueLength')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 221 && chargerNumber <= 224;
                    })
                    .map((data) => (
                    <td key={`queue-${data.charger.id}`} className="pivot-cell queue-cell">
                      <div className="queue-info">
                        <Users size={14} />
                        <span>{data.charger.queueLength}/{data.charger.maxQueueLength}</span>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Shift 1 Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.shift1')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 221 && chargerNumber <= 224;
                    })
                    .map((data) => {
                    const shift1User = data.queueDetails?.queueUsers.find(u => u.position === 1);
                    return (
                      <td key={`shift1-${data.charger.id}`} className="pivot-cell next-user-cell">
                        {shift1User ? (
                          <div className="next-user-info">
                            <div className="next-user-name">{shift1User.fullName}</div>
                            <div className="next-user-plate">{shift1User.primaryPlate}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', alignItems: 'center' }}>
                              <button
                                onClick={() => handleStartCharging(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift1User) || !data.charger.online || data.charger.status === 'charging'}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: (canUserPerformAction(shift1User) && data.charger.online && data.charger.status !== 'charging') ? '#10b981' : '#9ca3af',
                                  color: 'white',
                                  cursor: (canUserPerformAction(shift1User) && data.charger.online && data.charger.status !== 'charging') ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Start Charging"
                              >
                                <Play size={10} />
                                Start
                              </button>
                              <button
                                onClick={() => handleLeaveQueue(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift1User)}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: !canUserPerformAction(shift1User) ? '#9ca3af' : '#ef4444',
                                  color: 'white',
                                  cursor: !canUserPerformAction(shift1User) ? 'not-allowed' : 'pointer',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Leave Queue"
                              >
                                <UserMinus size={10} />
                                Leave
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="action-button-container">
                            <button
                              onClick={() => handleJoinQueue(data.charger, 1)}
                              disabled={actionLoading[data.charger.id] || false || !data.charger.online || isUserChargingAnywhere()}
                              style={{
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: (data.charger.online && !isUserChargingAnywhere()) ? '#3b82f6' : '#9ca3af',
                                color: 'white',
                                cursor: (data.charger.online && !isUserChargingAnywhere()) ? 'pointer' : 'not-allowed',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={isUserChargingAnywhere() ? "Already charging elsewhere" : "Join Queue"}
                            >
                              <UserPlus size={12} />
                              Join
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Shift 2 Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.shift2')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 221 && chargerNumber <= 224;
                    })
                    .map((data) => {
                    const shift2User = data.queueDetails?.queueUsers.find(u => u.position === 2);
                    return (
                      <td key={`shift2-${data.charger.id}`} className="pivot-cell next-user-cell">
                        {shift2User ? (
                          <div className="next-user-info">
                            <div className="next-user-name">{shift2User.fullName}</div>
                            <div className="next-user-plate">{shift2User.primaryPlate}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', alignItems: 'center' }}>
                              <button
                                onClick={() => handleStartCharging(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift2User) || !data.charger.online || data.charger.status === 'charging'}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: (canUserPerformAction(shift2User) && data.charger.online && data.charger.status !== 'charging') ? '#10b981' : '#9ca3af',
                                  color: 'white',
                                  cursor: (canUserPerformAction(shift2User) && data.charger.online && data.charger.status !== 'charging') ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Start Charging"
                              >
                                <Play size={10} />
                                Start
                              </button>
                              <button
                                onClick={() => handleLeaveQueue(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift2User)}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: canUserPerformAction(shift2User) ? '#ef4444' : '#9ca3af',
                                  color: 'white',
                                  cursor: canUserPerformAction(shift2User) ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Leave Queue"
                              >
                                <UserMinus size={10} />
                                Leave
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="action-button-container">
                            <button
                              onClick={() => handleJoinQueue(data.charger, 2)}
                              disabled={actionLoading[data.charger.id] || false || !data.charger.online || isUserChargingAnywhere()}
                              style={{
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: (data.charger.online && !isUserChargingAnywhere()) ? '#3b82f6' : '#9ca3af',
                                color: 'white',
                                cursor: (data.charger.online && !isUserChargingAnywhere()) ? 'pointer' : 'not-allowed',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={isUserChargingAnywhere() ? "Already charging elsewhere" : "Join Queue"}
                            >
                              <UserPlus size={12} />
                              Join
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Shift 3 Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.shift3')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 221 && chargerNumber <= 224;
                    })
                    .map((data) => {
                    const shift3User = data.queueDetails?.queueUsers.find(u => u.position === 3);
                    return (
                      <td key={`shift3-${data.charger.id}`} className="pivot-cell next-user-cell">
                        {shift3User ? (
                          <div className="next-user-info">
                            <div className="next-user-name">{shift3User.fullName}</div>
                            <div className="next-user-plate">{shift3User.primaryPlate}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', alignItems: 'center' }}>
                              <button
                                onClick={() => handleStartCharging(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift3User) || !data.charger.online || data.charger.status === 'charging'}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: (canUserPerformAction(shift3User) && data.charger.online && data.charger.status !== 'charging') ? '#10b981' : '#9ca3af',
                                  color: 'white',
                                  cursor: (canUserPerformAction(shift3User) && data.charger.online && data.charger.status !== 'charging') ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Start Charging"
                              >
                                <Play size={10} />
                                Start
                              </button>
                              <button
                                onClick={() => handleLeaveQueue(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift3User)}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: canUserPerformAction(shift3User) ? '#ef4444' : '#9ca3af',
                                  color: 'white',
                                  cursor: canUserPerformAction(shift3User) ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Leave Queue"
                              >
                                <UserMinus size={10} />
                                Leave
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="action-button-container">
                            <button
                              onClick={() => handleJoinQueue(data.charger, 3)}
                              disabled={actionLoading[data.charger.id] || false || !data.charger.online || isUserChargingAnywhere()}
                              style={{
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: (data.charger.online && !isUserChargingAnywhere()) ? '#3b82f6' : '#9ca3af',
                                color: 'white',
                                cursor: (data.charger.online && !isUserChargingAnywhere()) ? 'pointer' : 'not-allowed',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={isUserChargingAnywhere() ? "Already charging elsewhere" : "Join Queue"}
                            >
                              <UserPlus size={12} />
                              Join
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

              </tbody>
            </table>
          </div>
        </div>
        
        {/* Scroll to top button for first table */}
        <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
          <button
            onClick={scrollToTop}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#3b82f6',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            title="Scroll to top"
          >
            <ArrowUp size={16} />
            Scroll to top
          </button>
        </div>

        {/* Bottom Table: Chargers 225-228 */}
        <div className="excel-table-container">
          <div className="table-wrapper">
            <table className="excel-table pivot-table">
              <thead>
                <tr>
                  <th className="sticky-header row-label-header"></th>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 225 && chargerNumber <= 228;
                    })
                    .map((data) => (
                    <th key={data.charger.id} className="sticky-header charger-column">
                      {data.charger.name.replace(/\D/g, '')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Status Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.status')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 225 && chargerNumber <= 228;
                    })
                    .map((data) => (
                    <td key={`status-${data.charger.id}`} className="pivot-cell status-cell">
                      <div className="status-indicator">
                        <div 
                          className="status-icon"
                          style={{ color: getStatusColor(data.charger) }}
                        >
                          {getStatusIcon(data.charger)}
                        </div>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Current User Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.shift0')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 225 && chargerNumber <= 228;
                    })
                    .map((data) => (
                    <td key={`user-${data.charger.id}`} className="pivot-cell user-cell">
                      {data.charger.currentUser ? (
                        <div className="user-info">
                          <div className="user-name">{data.charger.currentUser.fullName}</div>
                          <div className="user-plate">{data.charger.currentUser.primaryPlate}</div>
                          <button
                            onClick={() => handleUpdateCharging(data.charger)}
                            disabled={actionLoading[data.charger.id] || !canUserPerformAction(data.charger.currentUser)}
                            style={{
                              padding: '4px 8px',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: canUserPerformAction(data.charger.currentUser) ? '#3b82f6' : '#9ca3af',
                              color: 'white',
                              cursor: canUserPerformAction(data.charger.currentUser) ? 'pointer' : 'not-allowed',
                              fontSize: '10px',
                              marginTop: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '2px'
                            }}
                            title="Update Session"
                          >
                            <Clock size={10} />
                            Update
                          </button>
                        </div>
                      ) : (
                        <div className="action-button-container">
                          <button
                            onClick={() => handleStartCharging(data.charger)}
                            disabled={actionLoading[data.charger.id] || false || !data.charger.online || isUserChargingAnywhere()}
                            style={{
                              padding: '6px 12px',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: (data.charger.online && !isUserChargingAnywhere()) ? '#10b981' : '#9ca3af',
                              color: 'white',
                              cursor: (data.charger.online && !isUserChargingAnywhere()) ? 'pointer' : 'not-allowed',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title={isUserChargingAnywhere() ? "Already charging elsewhere" : "Start Charging"}
                          >
                            <Play size={12} />
                            Start
                          </button>
                        </div>
                      )}
                    </td>
                  ))}
                </tr>

                {/* End Time Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.endTime')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 225 && chargerNumber <= 228;
                    })
                    .map((data) => (
                    <td key={`end-${data.charger.id}`} className="pivot-cell time-cell">
                      {data.charger.estimatedEndTime ? (
                        <span className={data.charger.isSessionOverdue ? 'overdue' : ''}>
                          {formatTime(data.charger.estimatedEndTime)}
                        </span>
                      ) : (
                        <span className="empty-cell">-</span>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Queue Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.queueLength')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 225 && chargerNumber <= 228;
                    })
                    .map((data) => (
                    <td key={`queue-${data.charger.id}`} className="pivot-cell queue-cell">
                      <div className="queue-info">
                        <Users size={14} />
                        <span>{data.charger.queueLength}/{data.charger.maxQueueLength}</span>
                      </div>
                    </td>
                  ))}
                </tr>

                {/* Shift 1 Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.shift1')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 225 && chargerNumber <= 228;
                    })
                    .map((data) => {
                    const shift1User = data.queueDetails?.queueUsers.find(u => u.position === 1);
                    return (
                      <td key={`shift1-${data.charger.id}`} className="pivot-cell next-user-cell">
                        {shift1User ? (
                          <div className="next-user-info">
                            <div className="next-user-name">{shift1User.fullName}</div>
                            <div className="next-user-plate">{shift1User.primaryPlate}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', alignItems: 'center' }}>
                              <button
                                onClick={() => handleStartCharging(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift1User) || !data.charger.online || data.charger.status === 'charging'}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: (canUserPerformAction(shift1User) && data.charger.online && data.charger.status !== 'charging') ? '#10b981' : '#9ca3af',
                                  color: 'white',
                                  cursor: (canUserPerformAction(shift1User) && data.charger.online && data.charger.status !== 'charging') ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Start Charging"
                              >
                                <Play size={10} />
                                Start
                              </button>
                              <button
                                onClick={() => handleLeaveQueue(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift1User)}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: canUserPerformAction(shift1User) ? '#ef4444' : '#9ca3af',
                                  color: 'white',
                                  cursor: canUserPerformAction(shift1User) ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Leave Queue"
                              >
                                <UserMinus size={10} />
                                Leave
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="action-button-container">
                            <button
                              onClick={() => handleJoinQueue(data.charger, 1)}
                              disabled={actionLoading[data.charger.id] || false || !data.charger.online || isUserChargingAnywhere()}
                              style={{
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: (data.charger.online && !isUserChargingAnywhere()) ? '#3b82f6' : '#9ca3af',
                                color: 'white',
                                cursor: (data.charger.online && !isUserChargingAnywhere()) ? 'pointer' : 'not-allowed',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={isUserChargingAnywhere() ? "Already charging elsewhere" : "Join Queue"}
                            >
                              <UserPlus size={12} />
                              Join
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Shift 2 Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.shift2')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 225 && chargerNumber <= 228;
                    })
                    .map((data) => {
                    const shift2User = data.queueDetails?.queueUsers.find(u => u.position === 2);
                    return (
                      <td key={`shift2-${data.charger.id}`} className="pivot-cell next-user-cell">
                        {shift2User ? (
                          <div className="next-user-info">
                            <div className="next-user-name">{shift2User.fullName}</div>
                            <div className="next-user-plate">{shift2User.primaryPlate}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', alignItems: 'center' }}>
                              <button
                                onClick={() => handleStartCharging(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift2User) || !data.charger.online || data.charger.status === 'charging'}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: (canUserPerformAction(shift2User) && data.charger.online && data.charger.status !== 'charging') ? '#10b981' : '#9ca3af',
                                  color: 'white',
                                  cursor: (canUserPerformAction(shift2User) && data.charger.online && data.charger.status !== 'charging') ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Start Charging"
                              >
                                <Play size={10} />
                                Start
                              </button>
                              <button
                                onClick={() => handleLeaveQueue(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift2User)}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: canUserPerformAction(shift2User) ? '#ef4444' : '#9ca3af',
                                  color: 'white',
                                  cursor: canUserPerformAction(shift2User) ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Leave Queue"
                              >
                                <UserMinus size={10} />
                                Leave
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="action-button-container">
                            <button
                              onClick={() => handleJoinQueue(data.charger, 2)}
                              disabled={actionLoading[data.charger.id] || false || !data.charger.online || isUserChargingAnywhere()}
                              style={{
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: (data.charger.online && !isUserChargingAnywhere()) ? '#3b82f6' : '#9ca3af',
                                color: 'white',
                                cursor: (data.charger.online && !isUserChargingAnywhere()) ? 'pointer' : 'not-allowed',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={isUserChargingAnywhere() ? "Already charging elsewhere" : "Join Queue"}
                            >
                              <UserPlus size={12} />
                              Join
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Shift 3 Row */}
                <tr>
                  <td className="row-label">{t('chargerTable.columns.shift3')}</td>
                  {chargersData
                    .filter(data => {
                      const chargerNumber = parseInt(data.charger.name.replace(/\D/g, ''));
                      return chargerNumber >= 225 && chargerNumber <= 228;
                    })
                    .map((data) => {
                    const shift3User = data.queueDetails?.queueUsers.find(u => u.position === 3);
                    return (
                      <td key={`shift3-${data.charger.id}`} className="pivot-cell next-user-cell">
                        {shift3User ? (
                          <div className="next-user-info">
                            <div className="next-user-name">{shift3User.fullName}</div>
                            <div className="next-user-plate">{shift3User.primaryPlate}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px', alignItems: 'center' }}>
                              <button
                                onClick={() => handleStartCharging(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift3User) || !data.charger.online || data.charger.status === 'charging'}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: (canUserPerformAction(shift3User) && data.charger.online && data.charger.status !== 'charging') ? '#10b981' : '#9ca3af',
                                  color: 'white',
                                  cursor: (canUserPerformAction(shift3User) && data.charger.online && data.charger.status !== 'charging') ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Start Charging"
                              >
                                <Play size={10} />
                                Start
                              </button>
                              <button
                                onClick={() => handleLeaveQueue(data.charger)}
                                disabled={actionLoading[data.charger.id] || !canUserPerformAction(shift3User)}
                                style={{
                                  padding: '4px 8px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  backgroundColor: canUserPerformAction(shift3User) ? '#ef4444' : '#9ca3af',
                                  color: 'white',
                                  cursor: canUserPerformAction(shift3User) ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title="Leave Queue"
                              >
                                <UserMinus size={10} />
                                Leave
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="action-button-container">
                            <button
                              onClick={() => handleJoinQueue(data.charger, 3)}
                              disabled={actionLoading[data.charger.id] || false || !data.charger.online || isUserChargingAnywhere()}
                              style={{
                                padding: '6px 12px',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: (data.charger.online && !isUserChargingAnywhere()) ? '#3b82f6' : '#9ca3af',
                                color: 'white',
                                cursor: (data.charger.online && !isUserChargingAnywhere()) ? 'pointer' : 'not-allowed',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              title={isUserChargingAnywhere() ? "Already charging elsewhere" : "Join Queue"}
                            >
                              <UserPlus size={12} />
                              Join
                            </button>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>

              </tbody>
            </table>
          </div>
        </div>
        
        {/* Scroll to top button for second table */}
        <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
          <button
            onClick={scrollToTop}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: '#3b82f6',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
            title="Scroll to top"
          >
            <ArrowUp size={16} />
            Scroll to top
          </button>
        </div>

        {/* Summary Statistics */}
        <div className="table-summary">
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">{t('chargerTable.summary.totalChargers')}</span>
              <span className="stat-value">{chargersData.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('chargerTable.summary.online')}</span>
              <span className="stat-value">
                {chargersData.filter(d => d.charger.online).length}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('chargerTable.summary.charging')}</span>
              <span className="stat-value">
                {chargersData.filter(d => d.charger.status === 'charging').length}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('chargerTable.summary.available')}</span>
              <span className="stat-value">
                {chargersData.filter(d => d.charger.online && d.charger.status === 'idle').length}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('chargerTable.summary.totalInQueue')}</span>
              <span className="stat-value">
                {chargersData.reduce((sum, d) => sum + d.charger.queueLength, 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Start Charging Modal */}
      {showStartModal && selectedCharger && (
        <StartChargingModal
          chargerName={selectedCharger.name}
          onStart={handleConfirmStartCharging}
          onCancel={() => {
            setShowStartModal(false);
            setSelectedCharger(null);
            setStartChargingError(null);
          }}
          isLoading={actionLoading[selectedCharger.id] || false}
          error={startChargingError}
        />
      )}

      {/* Update Charging Modal */}
      {showUpdateModal && selectedCharger && (
        <UpdateChargingModal
          chargerName={selectedCharger.name}
          estimatedEndTime={selectedCharger.estimatedEndTime}
          maxEndTime={selectedCharger.maxEndTime}
          isOverdue={selectedCharger.isSessionOverdue}
          startedAt={selectedCharger.startedAt}
          onStop={handleConfirmStopCharging}
          onExtend={handleConfirmExtendCharging}
          onReduce={handleConfirmReduceCharging}
          onCancel={() => {
            setShowUpdateModal(false);
            setSelectedCharger(null);
            setUpdateChargingError(null);
          }}
          isLoading={actionLoading[selectedCharger.id] || false}
          error={updateChargingError}
        />
      )}
    </div>
  );
}

interface StartChargingModalProps {
  chargerName: string;
  onStart: (minutes: number) => void;
  onCancel: () => void;
  isLoading: boolean;
  error?: string | null;
}

function StartChargingModal({ chargerName, onStart, onCancel, isLoading, error }: StartChargingModalProps) {
  const { t } = useTranslation();
  const [selectedMinutes, setSelectedMinutes] = useState(90);
  const [customMinutes, setCustomMinutes] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const presets = [
    { minutes: 60, label: t('dialogs.startCharging.presets.60'), subtitle: t('dialogs.startCharging.quickCharge') },
    { minutes: 90, label: t('dialogs.startCharging.presets.90'), subtitle: t('dialogs.startCharging.standard') },
    { minutes: 120, label: t('dialogs.startCharging.presets.120'), subtitle: t('dialogs.startCharging.extended') },
    { minutes: 180, label: t('dialogs.startCharging.presets.180'), subtitle: t('dialogs.startCharging.longCharge') }
  ];

  const handleStart = () => {
    const minutes = useCustom ? parseInt(customMinutes) || 90 : selectedMinutes;
    onStart(minutes);
  };

  return (
    <div className="modal-overlay-modern">
      <div className="modal-modern">
        <div className="modal-header-modern">
          <h3 className="modal-title">{t('dialogs.startCharging.title')}</h3>
          <p className="modal-subtitle">{chargerName}</p>
        </div>
        
        <div className="modal-content-modern">
          <p className="modal-description">{t('dialogs.startCharging.estimatedTime')}</p>
          
          {error && (
            <div className="queue-error-message">
              <div className="error-alert">
                <X size={16} />
                <span>{error}</span>
              </div>
            </div>
          )}
          
          <div className="time-presets-modern">
            {presets.map(({ minutes, label, subtitle }) => (
              <button
                key={minutes}
                className={`preset-btn-modern ${!useCustom && selectedMinutes === minutes ? 'preset-btn-active-modern' : ''}`}
                onClick={() => {
                  setSelectedMinutes(minutes);
                  setUseCustom(false);
                }}
              >
                <div className="preset-label">{label}</div>
                <div className="preset-subtitle">{subtitle}</div>
              </button>
            ))}
          </div>

          <div className="custom-time-modern">
            <button
              className={`custom-option-modern ${useCustom ? 'custom-option-active-modern' : ''}`}
              onClick={() => setUseCustom(true)}
            >
              <div className="custom-option-content">
                <span className="custom-option-label">{t('dialogs.startCharging.custom')}</span>
                {useCustom && (
                  <input
                    type="number"
                    value={customMinutes}
                    onChange={(e) => setCustomMinutes(e.target.value)}
                    placeholder={t('dialogs.startCharging.minutesPlaceholder')}
                    min="1"
                    max="240"
                    className="custom-input-modern"
                    autoFocus
                  />
                )}
              </div>
            </button>
          </div>
        </div>

        <div className="modal-actions-modern">
          <button
            className="btn-modern btn-secondary-modern"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </button>
          <button
            className="btn-modern btn-primary-modern"
            onClick={handleStart}
            disabled={isLoading}
          >
            <Zap size={16} />
            {isLoading ? t('common.loading') : t('dialogs.startCharging.start')}
          </button>
        </div>
      </div>
    </div>
  );
}